import React from 'react';
import { StatusBar as ExpoStatusBar } from 'expo-status-bar';
import { StatusBar as RNStatusBar, Platform, DeviceEventEmitter, View, StyleSheet, Alert } from 'react-native';
import { NavigationContainer } from '@react-navigation/native';
import { createStackNavigator } from '@react-navigation/stack';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import { GestureHandlerRootView } from 'react-native-gesture-handler';

// ═══ FIX 2026-04 : supprim\u00e9 ═══
// Anciennement : defaults globaux maxFontSizeMultiplier sur Text/TextInput via
// `RNText.defaultProps`. React 19 a retir\u00e9 le support de `defaultProps` sur
// les composants fonctionnels \u2192 crash au d\u00e9marrage (\u00e9cran blanc).
// Comportement par d\u00e9faut : allowFontScaling=true, pas de cap. Suffisant.
// Si besoin de re-cap le scaling, appliquer maxFontSizeMultiplier directement
// sur chaque <Text> dans l'app.

import { I18nProvider }          from './src/i18n';
import { ThemeProvider }         from './src/context/ThemeContext';
import { AuthProvider, useAuth } from './src/context/AuthContext';
import TabNavigator              from './src/navigation/TabNavigator';
import WebSidebar, { SIDEBAR_WIDTH } from './src/components/WebSidebar';
import { AppDialogHost }          from './src/components/AppDialog';
import { StripePaymentPopupHost } from './src/components/StripePaymentPopup';
import { ProcessingOverlayHost } from './src/components/ProcessingOverlay';
import { ShareSheetHost }        from './src/components/ShareSheet';
// v1.12.258 — CookieConsent React retiré (doublon avec inject-skimlinks.js)
// import CookieConsent              from './src/components/CookieConsent';
import { trackAppOpen, trackScreen, initWebAnalytics, setAnalyticsConsent } from './src/services/AnalyticsService';
import { useResponsive }         from './src/hooks/useResponsive';
import { useWebOAuthCallback }   from './src/hooks/useWebOAuthCallback';
import { useStripeCallback }     from './src/hooks/useStripeCallback';
import { setRootNavRef }         from './src/utils/rootNavigation';
import LoginScreen               from './src/screens/LoginScreen';
import SignUpScreen              from './src/screens/SignUpScreen';
import ConfirmEmailScreen        from './src/screens/ConfirmEmailScreen';
import ForgotPasswordScreen      from './src/screens/ForgotPasswordScreen';
import OnboardingScreen          from './src/screens/OnboardingScreen';
import RegionLanguageScreen      from './src/screens/RegionLanguageScreen';
import OrdersTrackingScreen      from './src/screens/OrdersTrackingScreen';
import DashboardScreen           from './src/screens/DashboardScreen';
import LooksScreen               from './src/screens/LooksScreen';
import SkimlinksTestScreen       from './src/screens/SkimlinksTestScreen';
import NotificationsScreen       from './src/screens/NotificationsScreen';
import EarningsScreen            from './src/screens/EarningsScreen';
import FavoritesScreen           from './src/screens/FavoritesScreen';
import CompleteProfileScreen     from './src/screens/CompleteProfileScreen';
import AdminDashboardScreen      from './src/screens/AdminDashboardScreen';
import TermsScreen               from './src/screens/TermsScreen';
import PrivacyScreen             from './src/screens/PrivacyScreen';
import ConditionsVenteScreen     from './src/screens/ConditionsVenteScreen';
import CalendarScreen            from './src/screens/CalendarScreen';
import SplashScreen              from './src/screens/SplashScreen';
import DailyPreferencesModal     from './src/components/DailyPreferencesModal';
import FreeOnboardingTryOnModal  from './src/components/FreeOnboardingTryOnModal';
import MobileConsentModal        from './src/components/MobileConsentModal';
import WebAppDownloadBanner      from './src/components/WebAppDownloadBanner';
import AddonsShopScreen          from './src/screens/AddonsShopScreen';
import { clearSessionPrefs, hydrateCache } from './src/services/DailyPreferencesService';
import { logDeviceInfo }         from './src/utils/ResponsiveUtils';

// Log les infos device au démarrage (debug responsive)
logDeviceInfo();

// v1.12.681 — Injecter gtag.js sur web le plus tôt possible (Google Analytics).
// Sans ça window.gtag n'existe pas → events tombaient sur le Measurement
// Protocol fetch qui échoue en CORS.
try { initWebAnalytics(); } catch {}

// v1.12.624 — Global error handlers pour identifier les crashs JS non gérés
// qui peuvent causer un reload silencieux + déconnexion sur web.
// FIX 185 : sur iOS 26.4.2 + React 19 + RN 0.83, `window` est polyfillé comme
// objet vide → `window.addEventListener` est undefined → crash "undefined is not
// a function" au démarrage (écran blanc). On gate strictement par Platform web.
if (Platform.OS === 'web' && typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
  window.addEventListener('error', function(e) {
    console.error('[App] 🚨 window error:', e.message, '| at:', e.filename, ':', e.lineno, ':', e.colno, '| stack:', e.error?.stack?.slice(0, 500));
  });
  window.addEventListener('unhandledrejection', function(e) {
    console.error('[App] 🚨 unhandled promise rejection:', e.reason?.message || e.reason, '| stack:', e.reason?.stack?.slice(0, 500));
  });
  window.addEventListener('beforeunload', function() {
    console.warn('[App] 🚨 page about to unload/reload — check the cause');
  });
  // v1.12.646 — Détection bfcache (Chrome back/forward cache).
  // Si l'user fait back navigateur depuis Stripe, Chrome restaure l'ancienne
  // page Mytenue depuis le bfcache au lieu de la recharger. Le `pageshow`
  // event fire avec persisted=true. On force un reload propre pour:
  //   - éviter d'afficher du contenu obsolète
  //   - réexécuter useStripeCallback qui flag pending → popup
  window.addEventListener('pageshow', function(e) {
    if (e.persisted) {
      console.log('[App] 🔄 page restored from bfcache → force reload');
      window.location.reload();
    }
  });

  // v1.12.656 — VRAI FIX AU CŒUR du bug Stripe re-redirect.
  // Cause racine identifiée via logs: après retour Stripe, l'historique browser
  // contient encore l'URL Stripe (checkout.stripe.com/c/pay/cs_test_XXX). Quand
  // React Nav fait un history.back() ou history.go(-N) en réponse à une nav tab
  // (ex: navigate('Home') depuis Profil), le browser charge la page Stripe
  // depuis le bfcache. Stripe redirige automatiquement vers /billing/cancel.
  // → Boucle infinie.
  //
  // Fix: monkey-patch window.history.back et window.history.go(négatif) pour
  // no-op tant que le flag sticky __stripe_session_block_auto__ est actif.
  // Les navigations forward (pushState via navigate) restent fonctionnelles
  // → l'user peut toujours naviguer entre les écrans via sidebar/tab bar.
  try {
    const _origBack = window.history.back.bind(window.history);
    const _origGo = window.history.go.bind(window.history);
    const isBlocked = () => {
      try { return window.sessionStorage?.getItem('__stripe_session_block_auto__') === '1'; }
      catch { return false; }
    };
    window.history.back = function() {
      if (isBlocked()) {
        console.warn('[Stripe] 🛡️ history.back() BLOCKED (post-Stripe — sinon bfcache restore Stripe URL)');
        return;
      }
      return _origBack();
    };
    window.history.go = function(delta) {
      if (typeof delta === 'number' && delta < 0 && isBlocked()) {
        console.warn('[Stripe] 🛡️ history.go(' + delta + ') BLOCKED (post-Stripe)');
        return;
      }
      return _origGo(delta);
    };
    console.log('[App] ✓ history.back/go monkey-patched for Stripe protection');
  } catch (e) {
    console.warn('[App] history monkey-patch failed:', e?.message);
  }
}

// Note : Le recovery AsyncStorage iOS 26 est dans index.js (entry point)
// pour s'exécuter AVANT que React ne soit monté.

// ═══ Import optionnel de expo-splash-screen ═══
// Si installé → on peut piloter le splash natif (preventAutoHide + hide manuel)
// Sinon → fallback silencieux, notre SplashScreen custom prend le relais
let ExpoSplash = null;
try {
  ExpoSplash = require('expo-splash-screen');
  ExpoSplash.preventAutoHideAsync().catch(() => {});
} catch {}

const Stack = createStackNavigator();

function RootNavigator() {
  const { isAuthenticated, user, loading, updateUserProfile, reloadProfile } = useAuth();
  const { isDesktopWeb } = useResponsive();
  const navigationRef = React.useRef(null);
  const [showOnboarding, setShowOnboarding] = React.useState(false);
  // v1.12.603 V5.5 — Free onboarding 1 Try-On gratuit
  const [showFreeTryon, setShowFreeTryon] = React.useState(false);
  // v1.12.992 — Consentement analytics OPT-IN mobile (1er lancement)
  const [showConsent, setShowConsent] = React.useState(false);
  // Parcours guidé nouveau user (popup d'activation après onboarding)
  // Premier login (nouveau user) → on supprime le popup "préférences du jour" cette session
  const firstLoginRef = React.useRef(false);

  // v1.12.119 — Re-hydrate le profil quand la page web redevient visible
  // après >2 min d'inactivité. Évite les sessions périmées (ex: gender
  // perdu → articles partenaires affichés comme F au lieu de M).
  React.useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    if (!isAuthenticated) return undefined;
    let hiddenAt = 0;
    const onVisibilityChange = () => {
      if (document.hidden) {
        hiddenAt = Date.now();
        return;
      }
      const elapsed = Date.now() - hiddenAt;
      // >2 min d'absence → recharger profil (gender, tier, etc.)
      if (hiddenAt > 0 && elapsed > 2 * 60 * 1000) {
        console.log(`[App] tab visible again after ${Math.round(elapsed / 1000)}s → reload profile`);
        try { reloadProfile?.(); } catch {}
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    // Aussi sur focus window (Safari peut ne pas émettre visibilitychange)
    window.addEventListener('focus', onVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('focus', onVisibilityChange);
    };
  }, [isAuthenticated, reloadProfile]);
  // v1.12.119 — bootDone : true dès que loading passe à false la 1re fois.
  // Empêche le SplashScreen de réapparaître pendant les actions auth (signin,
  // signup) qui setLoading(true) → sinon NavigationContainer démonté entre
  // 2 renders et la stack modal Login est détruite → user revoit Home après
  // un 401, illusion de "redirect en tant qu'invité".
  const [bootDone, setBootDone] = React.useState(false);
  React.useEffect(() => {
    if (!loading && !bootDone) setBootDone(true);
  }, [loading, bootDone]);

  // Expose la nav ref globalement pour rootNavigate / rootReplace
  // (utilisé par les flows auth qui doivent traverser des modal stacks)
  React.useEffect(() => {
    setRootNavRef(navigationRef);
  }, []);

  // v1.12.117 — sur web, capture le code OAuth Google ?code=... même si
  // LoginScreen n'est plus dans le stack (browse libre). Sans ça, après
  // retour Google, l'URL contient le code mais rien ne l'échange contre
  // des tokens → user reste non authentifié.
  useWebOAuthCallback();
  useStripeCallback();

  // ═══ Popup préférences quotidiennes (cold start) ═══
  // Au tout premier rendu de App.js (cold start), on efface les prefs de la
  // session précédente puis on programme l'affichage du popup une fois que
  // l'user est authentifié et l'onboarding passé.
  const [showDailyPrefs, setShowDailyPrefs] = React.useState(false);
  const [dailyPrefsConsumed, setDailyPrefsConsumed] = React.useState(false);

  // Reset des prefs au cold start (effet exécuté UNE seule fois au mount)
  React.useEffect(() => {
    (async () => {
      // ═══ Health check AsyncStorage au cold start ═══
      // Sur iOS 26+, des clés AsyncStorage corrompues peuvent crasher l'app
      // au démarrage (NSString:initWithContentsOfFile lance une exception ObjC
      // que le TurboModule ne peut pas catch → SIGABRT).
      // On scanne toutes les clés et on supprime celles qui throw à la lecture.
      try {
        const AsyncStorage = (await import('@react-native-async-storage/async-storage')).default;
        let allKeys = [];
        try { allKeys = (await AsyncStorage.getAllKeys()) || []; }
        catch (e) {
          console.warn('[App] AsyncStorage.getAllKeys failed, attempting full reset:', e?.message);
          try { await AsyncStorage.clear(); } catch (_) {}
          return;
        }
        const corruptedKeys = [];
        // Test chaque clé individuellement avec multiGet pour détecter les corrompues
        for (const k of allKeys) {
          try {
            const v = await AsyncStorage.getItem(k);
            // Vérifier que la valeur est bien une string (pas un objet sérialisé bizarre)
            if (v !== null && typeof v !== 'string') {
              corruptedKeys.push(k);
            }
          } catch (e) {
            console.warn('[App] AsyncStorage corrupt key detected:', k, '→ will remove');
            corruptedKeys.push(k);
          }
        }
        // Suppression des clés corrompues
        for (const k of corruptedKeys) {
          try { await AsyncStorage.removeItem(k); } catch (_) {}
        }
        if (corruptedKeys.length > 0) {
          console.log(`[App] cleaned ${corruptedKeys.length} corrupt AsyncStorage keys`);
        } else {
          console.log(`[App] AsyncStorage health check OK (${allKeys.length} keys)`);
        }
      } catch (e) {
        console.warn('[App] AsyncStorage health check failed:', e?.message);
      }

      // Reset des prefs daily (toujours, à chaque cold start)
      try {
        await clearSessionPrefs();
        console.log('[App] cold start → daily prefs cleared');
      } catch (_) {}
    })();

    // ═══ Ping CJ impressions au cold start ═══
    const cjPingTimer = setTimeout(() => {
      try {
        const cjModule = require('./src/services/CJProductsService');
        if (cjModule && typeof cjModule.pingCJImpression === 'function') {
          cjModule.pingCJImpression('10471851'); // Cashmere
          cjModule.pingCJImpression('15830678'); // iQueens
          console.log('[CJ] cold-start impressions sent');
        }
      } catch (e) {
        console.warn('[CJ] impression ping skipped:', e?.message || 'error');
      }
    }, 3000);

    return () => clearTimeout(cjPingTimer);
  }, []);

  // ═══ Reset de la consommation des prefs au changement d'auth/user ═══
  // Si l'user se déconnecte ou change de compte, on doit réafficher le popup
  // de prefs au prochain login.
  React.useEffect(() => {
    if (!isAuthenticated) {
      setDailyPrefsConsumed(false);
      setShowDailyPrefs(false);
    }
  }, [isAuthenticated]);

  // Reset spécifique au changement d'user.id (un user se logout puis un autre
  // se login dans la même session app — rare mais possible).
  const lastUserIdRef = React.useRef(null);
  React.useEffect(() => {
    const currentId = user?.id || null;
    if (lastUserIdRef.current && currentId && lastUserIdRef.current !== currentId) {
      // Changement d'user → on remet les prefs à zéro
      console.log('[App] user changed, resetting daily prefs');
      setDailyPrefsConsumed(false);
      setShowDailyPrefs(false);
      try {
        const { clearSessionPrefs, setCachedPrefs } = require('./src/services/DailyPreferencesService');
        clearSessionPrefs();
        setCachedPrefs(null);
      } catch {}
    }
    lastUserIdRef.current = currentId;
  }, [user?.id]);

  React.useEffect(() => {
    if (!isAuthenticated || !user) {
      setShowOnboarding(false);
      return;
    }
    // ═══ FIX 2026-04 : attendre l'hydratation DB avant de décider ═══
    // Avant : au moment où on entrait ici, user venait juste de buildUser
    // (JWT claims seulement). Les champs DB (birthDate, gender custom)
    // n'étaient pas encore chargés → onboarding s'affichait faussement.
    // Maintenant : on attend user.profileHydrated === true (posé par
    // loadSubscriptionForUser après GET /profile).
    if (!user.profileHydrated) {
      console.log('[App] ⏳ waiting for profile hydration before deciding onboarding');
      return;
    }

    // ═══ RÈGLE MÉTIER (2026-04-26 : règle B) ═══
    // Onboarding s'affiche UNIQUEMENT pour les vrais nouveaux users
    // (première connexion, user.isNewUser === true).
    // Pour tous les autres users (existants en DB) : JAMAIS d'onboarding,
    // même si silhouette/tailles/etc. sont vides. Ces champs seront demandés
    // ailleurs dans l'app (au moment de générer une tenue, profil, etc.).
    //
    // Avantage : pas de frustration pour les users existants après ajout
    // de nouveaux champs au schéma. Les nouveaux champs sont remplis
    // progressivement, pas via un onboarding bloquant.

    if (user.isNewUser === true) {
      console.log('[App] → user nouveau (isNewUser=true), onboarding affiché');
      setShowOnboarding(true);
    } else {
      console.log('[App] ✓ user existant, skip onboarding');
      setShowOnboarding(false);
    }
  }, [
    isAuthenticated, user?.id, user?.profileHydrated, user?.isNewUser,
  ]);

  // 🔔 Enregistrer le token push dès que l'utilisateur est connecté
  React.useEffect(() => {
    if (!isAuthenticated || !user?.id) return;
    (async () => {
      try {
        const { registerPushNotifications } = await import('./src/services/PushService');
        // Utiliser le token Cognito pour authentifier l'API
        let token = user.accessToken;
        if (!token) {
          try {
            const { getAuthToken } = await import('./src/services/authHelpers');
            token = await getAuthToken();
          } catch {}
        }
        await registerPushNotifications(token);
      } catch(e) { console.warn('[App] push setup:', e.message); }
    })();
  }, [isAuthenticated, user?.id]);

  // v1.13.35 — Pré-warm Sélection styliste dès le login (avant que Home s'affiche).
  // Déclenche le calcul/cache backend du combo {gender} en arrière-plan → la
  // sélection est déjà chaude quand l'user arrive sur l'Accueil (1er démarrage).
  React.useEffect(() => {
    if (!isAuthenticated || !user?.id) return;
    const g = user?.gender === 'M' ? 'M' : 'F';
    let cancelled = false;
    const t = setTimeout(() => {
      if (cancelled) return;
      try {
        const { fetchLooksStyliste } = require('./src/services/LooksApiService');
        fetchLooksStyliste({ gender: g }, { limit: 30 }).catch(() => {});
      } catch (_) {}
    }, 900); // léger délai : ne pas concurrencer le boot/onboarding
    return () => { cancelled = true; clearTimeout(t); };
  }, [isAuthenticated, user?.id, user?.gender]);

  // ═══ FIX 2026-04 : identifier l'user dans RevenueCat dès la connexion ═══
  // Sans ça, RevenueCat reste sur un anonymous ID ($RCAnonymousID) → les
  // achats sont enregistrés sur cet anonymous ID au lieu du userId Cognito.
  // Conséquences : multiples anonymous sans status dans le dashboard RC,
  // achats impossibles à restaurer entre devices, webhook ne peut pas
  // matcher le user en DB.
  // logIn(user.id) rattache l'anonymous ID actuel au userId → fusionne
  // les attributs et achats. Idempotent : safe à appeler à chaque changement.
  //
  // ═══ v1.7.59 : on ne fait PLUS que initIAP() ici ═══
  // L'identifyUser() (avec email + attributes) est désormais géré
  // exclusivement par AuthContext useEffect (introduit en v1.7.53), qui a
  // accès au user complet (email/name/country/locale). Faire identifyUser()
  // ici ET dans AuthContext provoquait un double logIn() → race condition →
  // erreur "no singleton instance" en surfaçage si AuthContext appelait
  // setEmail() avant que le configure() local n'ait fini son init natif.
  React.useEffect(() => {
    if (!isAuthenticated || !user?.id) return;
    (async () => {
      try {
        const { initIAP } = await import('./src/services/IAPService');
        await initIAP(user.id);
      } catch (e) {
        console.warn('[App] RC initIAP failed:', e.message);
      }
    })();
  }, [isAuthenticated, user?.id]);

  // ═══ v1.10.18 : Sync des compteurs depuis le backend après login ═══
  //
  //   Lorsque l'user se reconnecte (notamment après désinstall/reinstall où
  //   tous les caches AsyncStorage sont effacés), on doit récupérer ses
  //   compteurs depuis la DB AWS RDS pour qu'ils soient cohérents avec
  //   l'état réel du serveur.
  //
  //   - Try-ons : fetchTryOnUsage() → GET /tryon/usage → table tryon_usage
  //   - AI requests : déjà sync via PremiumService.getCurrentAIUsage() au mount
  //
  //   Ces fetches mettent aussi à jour les caches locaux AsyncStorage pour
  //   éviter le flash "..." la prochaine fois que l'user ouvre l'app.
  React.useEffect(() => {
    if (!isAuthenticated || !user?.id || !user?.accessToken) return;
    (async () => {
      try {
        const { fetchTryOnUsage } = await import('./src/services/TryOnService');
        const usage = await fetchTryOnUsage(user.accessToken);
        console.log(`[App] ✓ Sync Try-on usage : ${usage.count}/${usage.limit} (tier=${usage.tier})`);
      } catch (e) {
        console.warn('[App] sync Try-on usage failed:', e.message);
      }
      try {
        const { getAIUsageToday } = await import('./src/services/PremiumService');
        const aiCount = await getAIUsageToday(user.id, user);
        console.log(`[App] ✓ Sync AI usage : ${aiCount}`);
      } catch (e) {
        console.warn('[App] sync AI usage failed:', e.message);
      }
    })();
  }, [isAuthenticated, user?.id, user?.accessToken]);

  // ═══ Écoute event global "force_onboarding" (depuis ProfileScreen si profil incomplet) ═══
  React.useEffect(() => {
    const sub = DeviceEventEmitter.addListener('force_onboarding', () => {
      console.log('[App] 🔔 force_onboarding requested');
      setShowOnboarding(true);
    });
    return () => sub.remove();
  }, []);

  // v1.12.603 V5.5 — Au 1er login, fetch /onboarding/free-tryon (idempotent côté backend)
  // v1.12.607 — Ne pas dépendre de showOnboarding (qui peut être true au boot
  // si profil incomplet). On fetch toujours et on attend onboarding fini si actif.
  const onboardingTryonCheckedRef = React.useRef(null); // v1.12.993 — stocke user.id vérifié
  const showOnboardingRef = React.useRef(showOnboarding);
  React.useEffect(() => { showOnboardingRef.current = showOnboarding; }, [showOnboarding]);

  React.useEffect(() => {
    if (!isAuthenticated || !user?.id) {
      console.log('[App] 🎁 free-tryon useEffect skipped:', { isAuthenticated, hasUserId: !!user?.id });
      return;
    }
    // v1.12.993 — Le check est par COMPTE (user.id), pas par session. Au relaunch
    // l'app restaure d'abord la session précédente (ref se posait) → un signup/login
    // d'un autre compte ensuite était sauté → pas de popup free-tryon. On mémorise
    // l'id déjà vérifié et on re-check à chaque changement de compte.
    if (onboardingTryonCheckedRef.current === user.id) {
      console.log('[App] 🎁 free-tryon already checked for this user');
      return;
    }
    onboardingTryonCheckedRef.current = user.id;
    // v1.13.3 — Robustesse "garanti au démarrage" : on RÉESSAIE en cas d'échec
    // transitoire (token pas encore prêt, réseau, 5xx) au lieu de verrouiller le
    // ref pour toute la session. Sur échec définitif → on déverrouille (null) pour
    // un retry au prochain lancement/login (le backend reste idempotent).
    const tryGrant = async (attempt = 0) => {
      try {
        const { AWS_CONFIG } = require('./src/config/aws_config');
        const SecureStore = require('./src/utils/secureStore');
        const token = await SecureStore.getItemAsync('outfit_access');
        if (!token) {
          // token pas encore persisté → on attend un peu et on réessaie
          if (attempt < 6) { setTimeout(() => tryGrant(attempt + 1), 1500); return; }
          console.log('[App] 🎁 no token after retries → unlock for next launch');
          onboardingTryonCheckedRef.current = null;
          return;
        }
        // v1.12.991 — install-id persistant (anti-abus : 1 free try-on / appareil).
        let installId = null;
        try {
          installId = await SecureStore.getItemAsync('outfit_install_id');
          if (!installId) {
            installId = 'inst_' + Date.now().toString(36) + Math.random().toString(36).slice(2, 12) + Math.random().toString(36).slice(2, 12);
            await SecureStore.setItemAsync('outfit_install_id', installId);
          }
        } catch (_) {}
        console.log('[App] 🎁 calling /onboarding/free-tryon (try', attempt, ') device=', installId ? installId.slice(0, 10) : 'none');
        const res = await fetch(`${AWS_CONFIG.apiUrl}/onboarding/free-tryon`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ device_id: installId }),
        });
        console.log('[App] 🎁 response status:', res.status);
        if (!res.ok) {
          // 5xx/réseau → retry, sinon déverrouille pour le prochain lancement
          if (res.status >= 500 && attempt < 3) { setTimeout(() => tryGrant(attempt + 1), 2500); return; }
          if (res.status >= 500) onboardingTryonCheckedRef.current = null;
          return;
        }
        const data = await res.json();
        console.log('[App] 🎁 response data:', data);
        if (data.granted) {
          console.log('[App] 🎁 ✅ granted — waiting onboarding to finish then show modal');
          const showWhenReady = (att = 0) => {
            if (showOnboardingRef.current && att < 30) {
              setTimeout(() => showWhenReady(att + 1), 1000);
            } else {
              setShowFreeTryon(true);
            }
          };
          setTimeout(() => showWhenReady(0), 1500);
        } else if (data.already) {
          console.log('[App] 🎁 ⏭ already granted, no modal');
        }
        // succès (granted/already/device_already) → ref reste verrouillé (pas de re-spam)
      } catch (e) {
        console.warn('[App] 🎁 free onboarding try-on check failed (try', attempt, '):', e?.message);
        if (attempt < 3) { setTimeout(() => tryGrant(attempt + 1), 2500); return; }
        onboardingTryonCheckedRef.current = null; // déverrouille pour retry au prochain lancement
      }
    };
    tryGrant(0);
  }, [isAuthenticated, user?.id]);

  // v1.12.993 — Consentement analytics OPT-IN mobile (1er lancement).
  // Affiché APRÈS l'onboarding (sinon masqué derrière l'écran onboarding plein
  // écran d'un nouvel user). Device-global (RGPD : demandé une fois, choix respecté).
  const consentCheckedRef = React.useRef(false);
  React.useEffect(() => {
    if (Platform.OS === 'web') return; // web : bandeau dédié (inject-skimlinks)
    if (showOnboarding) return;        // attendre la fin de l'onboarding
    if (consentCheckedRef.current) return;
    let cancelled = false;
    (async () => {
      try {
        const AsyncStorage = require('@react-native-async-storage/async-storage').default;
        const v = await AsyncStorage.getItem('outfit_cookie_consent');
        if (!cancelled && v !== 'accepted' && v !== 'declined') {
          consentCheckedRef.current = true;
          setShowConsent(true);
        }
      } catch (_) {}
    })();
    return () => { cancelled = true; };
  }, [showOnboarding, isAuthenticated]);

  // ═══ Programmer l'affichage du popup préférences ═══
  // Une fois l'user authentifié + onboarding passé + popup pas encore consommé
  // dans cette session, on l'affiche après un petit délai (laisse le temps au
  // RootNavigator de se monter pour éviter un flash).
  // ⚠️ FIX 2026-04-26 : ce useEffect DOIT être avant tout early return
  // pour éviter "Rendered fewer hooks than expected" (React rules of hooks).
  // v1.12.183 : TTL 24h calendaire — si l'utilisateur a déjà répondu
  // (skip ou submit) aujourd'hui (Paris TZ), on ne réaffiche pas la modale
  // au reload de la page.
  React.useEffect(() => {
    if (!isAuthenticated) return;
    if (showOnboarding) return;
    if (dailyPrefsConsumed) return;
    // PREMIER LOGIN (onboarding tout juste terminé) → on NE montre PAS le popup
    // "préférences du jour" (le nouvel user découvre l'app sans être interrompu).
    // On débloque quand même la génération styliste via l'event. Aux sessions
    // suivantes → comportement normal.
    if (firstLoginRef.current) {
      setDailyPrefsConsumed(true);
      try { DeviceEventEmitter.emit('daily_prefs_resolved', { skipped: true, firstLogin: true }); } catch {}
      return;
    }
    let cancelled = false;
    let timer = null;
    (async () => {
      try {
        const { hasResolvedToday } = require('./src/services/DailyPreferencesService');
        const already = await hasResolvedToday(user?.id);
        if (cancelled) return;
        if (already) {
          setDailyPrefsConsumed(true);
          // Émettre tout de suite l'event pour que les écrans qui dépendent
          // de la résolution des prefs (génération sélection styliste, etc.)
          // ne restent pas bloqués en attente.
          try { DeviceEventEmitter.emit('daily_prefs_resolved', { skipped: true, fromCache: true }); } catch {}
          return;
        }
      } catch (e) {
        console.warn('[App] hasResolvedToday failed:', e?.message);
      }
      if (cancelled) return;
      timer = setTimeout(() => setShowDailyPrefs(true), 1500);
    })();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [isAuthenticated, showOnboarding, dailyPrefsConsumed, user?.id]);

  // ═══ Listener : ouvrir le popup prefs à la demande ═══
  // Permet à Home/Profile de réouvrir le popup via :
  //   DeviceEventEmitter.emit('open_daily_prefs');
  // ⚠️ FIX 2026-04-26 : doit aussi être avant l'early return.
  React.useEffect(() => {
    if (!isAuthenticated) return;
    const sub = DeviceEventEmitter.addListener('open_daily_prefs', () => {
      console.log('[App] open_daily_prefs event received');
      setShowDailyPrefs(true);
    });
    return () => sub.remove();
  }, [isAuthenticated]);

  // v1.13.22 — Diagnostic freeze retiré (le point de gel a été localisé :
  // styliste-render-cards / chemin réinstall, corrigé). On purge silencieusement
  // l'ancien breadcrumb résiduel pour ne plus jamais afficher de message aux users.
  React.useEffect(() => {
    if (Platform.OS === 'web') return;
    (async () => {
      try {
        const AS = (await import('@react-native-async-storage/async-storage')).default;
        await AS.removeItem('__lf_step').catch(() => {});
      } catch (_) {}
    })();
  }, []);

  const handleOnboardingComplete = async (data) => {
    // Le OnboardingScreen a déjà appelé updateUserProfile côté context
    // et persisté sur le backend. Ici on force juste la fermeture.
    // ═══ Poser le flag "onboarding fait" pour ne plus redemander ═══
    try {
      const AsyncStorage = (await import('@react-native-async-storage/async-storage')).default;
      const ONBOARDING_KEY = `outfit_onboarding_done_${user?.id || user?.sub}`;
      await AsyncStorage.setItem(ONBOARDING_KEY, 'true');
      console.log('[App] ✓ onboarding flag posé en local');
    } catch (e) {
      console.warn('[App] onboarding flag save failed:', e.message);
    }
    // Premier login : ne pas afficher le popup "préférences du jour" cette session.
    firstLoginRef.current = true;
    setShowOnboarding(false);
  };

  // ═══ Early return AUTORISÉ ici : tous les hooks ont été appelés ═══
  if (isAuthenticated && showOnboarding) {
    return <OnboardingScreen onComplete={handleOnboardingComplete} />;
  }

  // ═══ FIX 2026-04 (v1.7.77) : un seul écran de chargement ═══
  // Avant : si auth `loading=true` après que le splash soit terminé, on
  //   affichait un ActivityIndicator pendant 1-3s → 2 écrans de chargement
  //   visuellement distincts s'enchaînaient (Splash custom → Loader simple
  //   → LoginScreen avec logo). UX confuse, on voyait le logo 3 fois.
  // Maintenant : tant que l'auth n'est pas décidée, on continue à afficher
  //   le SplashScreen (avec sa barre de progression à 100%). L'utilisateur
  //   ne voit qu'un seul écran de chargement avant de basculer sur LoginScreen
  //   (si non auth) ou TabNavigator (si auth).
  // v1.12.119 — N'afficher SplashScreen qu'au boot initial (1re fois que
  // loading=true). Les loading suivants (signin, signup, etc.) ne doivent
  // PAS démonter la stack — sinon le modal Login disparaît pendant la
  // requête et l'user atterrit sur Home après un 401.
  if (loading && !bootDone) {
    return <SplashScreen progress={1.0} label="Connexion…" />;
  }

  // v1.12.117 — sur web on autorise le browse libre (pas d'auth gate auto).
  // L'utilisateur peut explorer Home/Catalogue. Looks et Add-item dans Dressing
  // déclenchent une navigation vers Login. Sur natif (iOS/Android) on garde
  // l'auth gate au cold start (TestFlight reviewers, App Store guidelines).
  const isWeb = Platform.OS === 'web';
  const allowBrowseAsGuest = isWeb;
  // Sur web, Main est toujours rendu. Sur natif, gated par isAuthenticated.
  const showMainStack = isAuthenticated || allowBrowseAsGuest;
  const showWebSidebar = showMainStack && isDesktopWeb;

  // v1.12.152 — config linking pour deep-linking URL (web + native)
  // Permet d'accéder aux pages légales via www.mytenue.com/privacy etc.
  // (sans ça, le SPA fallback de Vercel charge juste la home).
  const linking = {
    prefixes: ['https://www.mytenue.com', 'https://mytenue.com', 'outfitapp://', 'outfit://'],
    config: {
      screens: {
        Privacy:        'privacy',
        Terms:          'terms',
        ConditionsVente: 'cgv',
        Login:          'login',
        SignUp:         'signup',
        ConfirmEmail:   'confirm-email',
        ForgotPassword: 'forgot-password',
        Onboarding:     'onboarding',
        Main: {
          path: '',
          screens: {
            Home:          '',
            'Garde-robe':  'wardrobe',
            Catalogue:     'catalogue',
            Suggestions:   'looks',
            Voyage:        'voyage',
            Profil:        'profil',
            Earnings:      'cashback',
            Favorites:     'favoris',
          },
        },
      },
    },
  };

  const navigatorTree = (
    <NavigationContainer
      ref={navigationRef}
      linking={linking}
      onReady={() => {
        try {
          trackAppOpen();
          const r = navigationRef.current?.getCurrentRoute?.();
          if (r?.name) trackScreen(r.name);
        } catch {}
      }}
      onStateChange={() => {
        try {
          const r = navigationRef.current?.getCurrentRoute?.();
          if (r?.name) trackScreen(r.name);
        } catch {}
      }}
    >
      {/* ═══ FIX 2026-04 : StatusBar Android non-translucente ═══
          En Expo Go, l'app s'étend par défaut sous la status bar système
          (translucent=true), ce qui masquait le logo OUTFIT. On force
          translucent=false + backgroundColor blanc pour qu'Android
          réserve l'espace de la status bar automatiquement.
          Sur iOS : expo-status-bar gère la teinte des icônes. */}
      {Platform.OS === 'android' && (
        <RNStatusBar translucent={false} backgroundColor="#FFFFFF" barStyle="dark-content" />
      )}
      <ExpoStatusBar style="dark" />
      <Stack.Navigator screenOptions={{ headerShown: false }}>
        {showMainStack ? (
          <>
            <Stack.Screen name="Main"          component={TabNavigator}         />
            <Stack.Screen name="Region"        component={RegionLanguageScreen} />
            <Stack.Screen name="Orders"        component={OrdersTrackingScreen} />
            <Stack.Screen name="Dashboard"     component={DashboardScreen}      />
            <Stack.Screen name="Looks"         component={LooksScreen}          />
            <Stack.Screen name="SkimlinksTest" component={SkimlinksTestScreen}  />
            <Stack.Screen name="Alertes"       component={NotificationsScreen}  />
            <Stack.Screen name="Earnings"      component={EarningsScreen}       />
            <Stack.Screen name="Favorites"     component={FavoritesScreen}      />
            <Stack.Screen name="CompleteProfile" component={CompleteProfileScreen} />
            <Stack.Screen name="AdminDashboard" component={AdminDashboardScreen} />
            <Stack.Screen name="Terms"         component={TermsScreen}          />
            <Stack.Screen name="Privacy"       component={PrivacyScreen}        />
            <Stack.Screen name="ConditionsVente" component={ConditionsVenteScreen} />
            {/* v1.12.190 — Calendrier de tenues */}
            <Stack.Screen name="Calendar"      component={CalendarScreen}       />
            {/* v1.12.608 — Boutique add-ons standalone */}
            <Stack.Screen name="Shop"          component={AddonsShopScreen}     />
            {/* Login + SignUp disponibles en modal pour browse libre web */}
            <Stack.Screen
              name="Login"
              component={LoginScreen}
              options={{ presentation: 'modal', animationTypeForReplace: 'pop' }}
            />
            <Stack.Screen
              name="SignUp"
              component={SignUpScreen}
              options={{ presentation: 'modal' }}
            />
            <Stack.Screen
              name="ConfirmEmail"
              component={ConfirmEmailScreen}
              options={{ presentation: 'modal' }}
            />
            <Stack.Screen
              name="ForgotPassword"
              component={ForgotPasswordScreen}
              options={{ presentation: 'modal' }}
            />
          </>
        ) : (
          <>
            <Stack.Screen name="Login" component={LoginScreen}
              options={{ animationTypeForReplace: 'pop' }} />
            <Stack.Screen name="SignUp"          component={SignUpScreen} />
            <Stack.Screen name="ConfirmEmail"    component={ConfirmEmailScreen} />
            <Stack.Screen name="ForgotPassword"  component={ForgotPasswordScreen} />
            <Stack.Screen name="Terms"   component={TermsScreen}   />
            <Stack.Screen name="Privacy" component={PrivacyScreen} />
            <Stack.Screen name="ConditionsVente" component={ConditionsVenteScreen} />
          </>
        )}
      </Stack.Navigator>
      {/* v1.12.992 — Consentement analytics opt-in (mobile, 1er lancement) */}
      <MobileConsentModal
        visible={showConsent}
        onAccept={() => { setAnalyticsConsent('accepted'); try { DeviceEventEmitter.emit('cookie_consent_changed', { choice: 'accepted' }); } catch (e) {} setShowConsent(false); }}
        onDecline={() => { setAnalyticsConsent('declined'); try { DeviceEventEmitter.emit('cookie_consent_changed', { choice: 'declined' }); } catch (e) {} setShowConsent(false); }}
      />
      {/* v1.13.8 — Bandeau web : inviter à télécharger l'app mobile (no-op sur natif) */}
      <WebAppDownloadBanner />
      {/* v1.12.603 V5.5 — Modal cadeau "1 Try-On offert" au 1er login */}
      {isAuthenticated && (
        <FreeOnboardingTryOnModal
          visible={showFreeTryon}
          onClose={() => setShowFreeTryon(false)}
          onTryNow={() => {
            // v1.12.611 — Bug "déconnexion" : navigate('Main', { screen: 'Catalogue' })
            // pouvait reset la stack racine et faire passer l'user par le RootNavigator
            // re-évaluation → écran Login. Fix : navigate direct vers 'Catalogue'
            // (React Navigation trouve le screen dans le tree imbriqué) + pas de
            // setTimeout qui créait une race entre démontage modal et re-render.
            setShowFreeTryon(false);
            try {
              navigationRef.current?.navigate?.('Catalogue');
            } catch (e) {
              console.warn('[App] navigate to Catalogue failed:', e?.message);
            }
          }}
        />
      )}

      {/* ═══ Popup préférences du jour (cold start uniquement) ═══ */}
      {isAuthenticated && (
        <DailyPreferencesModal
          visible={showDailyPrefs}
          onClose={() => {
            setShowDailyPrefs(false);
            setDailyPrefsConsumed(true);
            // v1.12.183 : marquer comme résolu pour aujourd'hui (Paris TZ)
            // → la modale ne réapparaîtra plus au reload de la page.
            try {
              const { markResolvedToday } = require('./src/services/DailyPreferencesService');
              markResolvedToday(user?.id);
            } catch {}
            // v1.12.122 — émet un event que HomeScreen écoute pour
            // déclencher la génération de la sélection styliste UNE seule
            // fois (au démarrage, après prefs OU skip).
            try { DeviceEventEmitter.emit('daily_prefs_resolved', { skipped: true }); } catch {}
          }}
          onSubmit={(prefs) => {
            console.log('[App] daily prefs saved:', prefs);
            // v1.12.183 : aussi marquer comme résolu sur submit
            try {
              const { markResolvedToday } = require('./src/services/DailyPreferencesService');
              markResolvedToday(user?.id);
            } catch {}
            try { DeviceEventEmitter.emit('daily_prefs_resolved', { skipped: false, prefs }); } catch {}
          }}
        />
      )}
    </NavigationContainer>
  );

  if (showWebSidebar) {
    return (
      <View style={{ flex: 1, flexDirection: 'row', backgroundColor: '#F5EFE8' }}>
        <WebSidebar navRef={navigationRef} />
        <View style={{ flex: 1, minWidth: 0, alignItems: 'center', backgroundColor: '#F5EFE8' }}>
          <View style={{
            flex: 1,
            width: '100%',
            maxWidth: 720,           // clamp lecture confortable (cards ne deviennent pas géantes)
            backgroundColor: '#FFFFFF',
            // position relative → les AppModal en position absolute sont
            // contraints à ce cadre (la sidebar gauche reste visible).
            ...(Platform.OS === 'web' ? { boxShadow: '0 0 32px rgba(0,0,0,0.08)', position: 'relative', overflow: 'hidden' } : {}),
          }}>
            {navigatorTree}
          </View>
        </View>
      </View>
    );
  }

  return navigatorTree;
}

export default function App() {
  // ═══ Splash avec barre de progression ═══
  // Géré ici (hors AuthProvider) pour s'afficher dès le tout premier démarrage,
  // même avant que l'auth Cognito commence à charger.
  const [splashProgress, setSplashProgress] = React.useState(0);
  const [splashLabel, setSplashLabel]       = React.useState(() => {
    try { const { getCurrentLang } = require('./src/i18n'); return getCurrentLang() === 'en' ? 'Initializing…' : 'Initialisation…'; } catch { return 'Initialisation…'; }
  });
  const [splashDone, setSplashDone]         = React.useState(false);

  React.useEffect(() => {
    // Masquer immédiatement le splash natif Expo → notre custom prend le relais
    if (ExpoSplash) ExpoSplash.hideAsync().catch(() => {});

    // ═══ PRÉCHARGEMENT PARALLÈLE ═══
    // Lancer le fetch du catalogue dynamique + météo en arrière-plan dès maintenant
    // Comme ça quand l'app monte, les données sont déjà prêtes (ou en cache)
    (async () => {
      try {
        const { fetchDynamicCatalog } = await import('./src/services/DynamicCatalogService');
        fetchDynamicCatalog('F').catch(() => {}); // fire-and-forget
      } catch {}
    })();

    // v1.12.268 — labels lang-aware via getCurrentLang() (SplashScreen rendu hors I18nProvider)
    let _splashLang = 'fr';
    try { const { getCurrentLang } = require('./src/i18n'); _splashLang = getCurrentLang() || 'fr'; } catch {}
    const isEn = _splashLang === 'en';
    const steps = [
      { delay: 50,   pct: 0.25, label: isEn ? 'Initializing…' : 'Initialisation…' },
      { delay: 350,  pct: 0.55, label: isEn ? 'Loading…'      : 'Chargement…' },
      { delay: 700,  pct: 0.85, label: isEn ? 'Preparing…'    : 'Préparation…' },
      { delay: 1000, pct: 1.00, label: isEn ? 'Ready!'        : 'Prêt !' },
    ];
    const timers = steps.map(s => setTimeout(() => {
      setSplashProgress(s.pct);
      setSplashLabel(s.label);
    }, s.delay));
    // Masquer le splash après 1.2s (suffisant pour voir la barre, rapide pour l'UX)
    const hideSplash = setTimeout(() => setSplashDone(true), 1200);
    timers.push(hideSplash);
    return () => timers.forEach(clearTimeout);
  }, []);

  // Splash custom tant que pas terminé
  if (!splashDone) {
    return <SplashScreen progress={splashProgress} label={splashLabel} />;
  }

  // Une fois le splash terminé → chargement des providers + app réelle
  // GestureHandlerRootView fond noir pour éviter flash blanc pendant la transition
  return (
    <ThemeProvider>
      <GestureHandlerRootView style={{ flex: 1, backgroundColor: '#0E0C0A' }}>
        <WebShell>
          <SafeAreaProvider>
            <I18nProvider>
              <AuthProvider>
                <RootNavigator />
              </AuthProvider>
            </I18nProvider>
          </SafeAreaProvider>
          {/* Host des dialogs brandés MyTenue (remplace window.alert sur web) */}
          <AppDialogHost />
          {/* v1.12.646 — Popup dédiée aux confirmations Stripe (au-dessus de tout) */}
          <StripePaymentPopupHost />
          <ProcessingOverlayHost />
          <ShareSheetHost />
          {/* v1.12.258 — CookieConsent React supprimé : doublon avec
              scripts/inject-skimlinks.js qui gère déjà GA/Skim/Impact. */}
        </WebShell>
      </GestureHandlerRootView>
    </ThemeProvider>
  );
}

/**
 * WebShell — adaptatif selon viewport web :
 *  - Native (iOS/Android) : passthrough complet
 *  - Web mobile (<768px)  : frame centrée 480px max + gouttières sombres
 *  - Web desktop (≥768px) : pleine largeur (la sidebar gauche est dans
 *                           RootNavigator). Pas de gouttières ni d'ombre.
 *
 * v1.12.117 — refonte web app desktop (Option B).
 */
function WebShell({ children }) {
  if (Platform.OS !== 'web') return children;
  return <WebShellWeb>{children}</WebShellWeb>;
}

function WebShellWeb({ children }) {
  const { isDesktopWeb } = useResponsive();
  // v1.12.119 — Wheel-event forwarder global
  // Avant : sur desktop la molette ne scrollait que si la souris était DANS le
  // panneau central (overflow:hidden coupe la propagation des wheels). En sur-
  // volant la sidebar ou les marges grises, rien ne se passait.
  // Maintenant : on capte tous les wheel events au niveau document et on les
  // applique au premier conteneur scrollable visible (overflow-y: auto/scroll
  // ET scrollHeight > clientHeight). preventDefault pour éviter double scroll.
  React.useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const findScrollable = () => {
      const candidates = document.querySelectorAll('div');
      for (const el of candidates) {
        if (!(el.scrollHeight > el.clientHeight)) continue;
        const cs = window.getComputedStyle(el);
        if (cs.overflowY === 'auto' || cs.overflowY === 'scroll') {
          // Vérifier que l'élément est visible et a une taille
          const rect = el.getBoundingClientRect();
          if (rect.width > 0 && rect.height > 0) return el;
        }
      }
      return null;
    };
    const onWheel = (e) => {
      // Si déjà au-dessus d'un scrollable qui scrolle, on laisse faire
      let cur = e.target;
      while (cur && cur !== document.body) {
        if (cur.scrollHeight > cur.clientHeight) {
          const cs = window.getComputedStyle(cur);
          if (cs.overflowY === 'auto' || cs.overflowY === 'scroll') return;
        }
        cur = cur.parentElement;
      }
      // Sinon, on forward au scrollable actif
      const target = findScrollable();
      if (target) {
        target.scrollTop += e.deltaY;
        e.preventDefault();
      }
    };
    document.addEventListener('wheel', onWheel, { passive: false });
    return () => document.removeEventListener('wheel', onWheel);
  }, []);

  if (isDesktopWeb) {
    // Sur desktop, on n'enferme plus dans une frame 480 — RootNavigator
    // gérera la sidebar + contenu en row.
    return <View style={webShellStyles.desktop}>{children}</View>;
  }
  // Mobile web : frame mobile centrée comme avant
  return (
    <View style={webShellStyles.outerMobile}>
      <View style={webShellStyles.frameMobile}>
        {children}
      </View>
    </View>
  );
}

const webShellStyles = StyleSheet.create({
  desktop: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  outerMobile: {
    flex: 1,
    alignItems: 'center',
    backgroundColor: '#0E0C0A',
  },
  frameMobile: {
    flex: 1,
    width: '100%',
    maxWidth: 480,
    overflow: 'hidden',
    ...(Platform.OS === 'web' ? { boxShadow: '0 0 24px rgba(0,0,0,0.18)' } : {}),
  },
});
